import React, { Component } from 'react';
import { XIcon, CheckCircleIcon, ClockIcon, PackageIcon, XCircleIcon, CalendarIcon, UserIcon, MapPinIcon, CreditCardIcon, FileTextIcon, TruckIcon, ExternalLinkIcon, StoreIcon } from 'lucide-react';
interface PrescriptionItem {
  name: string;
  quantity: number;
  price: number;
  image: string;
}
interface TrackingEvent {
  status: string;
  timestamp: string;
  description: string;
}
interface PrescriptionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  prescription: {
    id: string;
    date: string;
    status: string;
    items: PrescriptionItem[];
    doctor?: string;
    deliveryAddress?: string;
    deliveryCoordinates?: {
      lat: number;
      lng: number;
    };
    pickupBranch?: {
      name: string;
      address: string;
      coordinates: {
        lat: number;
        lng: number;
      };
    };
    pickupTime?: string;
    paymentMethod?: string;
    total?: number;
    prescriptionSource?: 'cash' | 'wasfaty' | 'insurance';
    insuranceProvider?: string;
    wasfatyNumber?: string;
    trackingHistory?: TrackingEvent[];
  };
}
export function PrescriptionDetailsModal({
  isOpen,
  onClose,
  prescription
}: PrescriptionDetailsModalProps) {
  if (!isOpen) return null;
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return <CheckCircleIcon className="w-6 h-6 text-green-500" data-id="element-4857" data-mp="11fnqeid0s2x5" />;
      case 'ready':
        return <PackageIcon className="w-6 h-6 text-blue-500" data-id="element-4858" data-mp="16ce6v6sql0j3" />;
      case 'in-progress':
        return <ClockIcon className="w-6 h-6 text-yellow-500" data-id="element-4859" data-mp="1bb7n36zer5my" />;
      default:
        return <XCircleIcon className="w-6 h-6 text-red-500" data-id="element-4860" data-mp="1gau13in28pwt" />;
    }
  };
  const getStatusText = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'Delivered';
      case 'ready':
        return 'Ready for Pickup';
      case 'in-progress':
        return 'In Progress';
      default:
        return 'Rejected';
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'ready':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'in-progress':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      default:
        return 'bg-red-50 text-red-700 border-red-200';
    }
  };
  const getPrescriptionSourceBadge = () => {
    if (!prescription.prescriptionSource) return null;
    const badges = {
      cash: {
        label: 'Cash Prescription',
        color: 'bg-gray-100 text-gray-700'
      },
      wasfaty: {
        label: 'Wasfaty',
        color: 'bg-blue-100 text-blue-700'
      },
      insurance: {
        label: 'Insurance',
        color: 'bg-green-100 text-green-700'
      }
    };
    const badge = badges[prescription.prescriptionSource];
    return <div className="mb-4" data-id="element-4861" data-mp="1uzgpfpkjky1r">
        <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold ${badge.color}`} data-id="element-4862" data-mp="1q044599bjz1a">
          <FileTextIcon className="w-3.5 h-3.5" data-id="element-4863" data-mp="29mxjr00kh4xd" />
          {badge.label}
          {prescription.prescriptionSource === 'wasfaty' && prescription.wasfatyNumber && <span className="ml-1" data-id="element-4864" data-mp="20lr0wkag3i0y">• {prescription.wasfatyNumber}</span>}
          {prescription.prescriptionSource === 'insurance' && prescription.insuranceProvider && <span className="ml-1" data-id="element-4865" data-mp="1quwar4u2vya8">• {prescription.insuranceProvider}</span>}
        </span>
      </div>;
  };
  const getGoogleMapsLink = (coordinates?: {
    lat: number;
    lng: number;
  }, address?: string) => {
    if (coordinates) {
      return `https://www.google.com/maps/dir/?api=1&destination=${coordinates.lat},${coordinates.lng}`;
    }
    if (address) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    }
    return null;
  };
  return <div className="fixed inset-0 z-[200] overflow-y-auto" data-id="element-4866" data-mp="1cubuafbmyejw">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0" data-id="element-4867" data-mp="14unxn9pis1mm">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} data-id="element-4868" data-mp="zwe22xzvuz0h"></div>
        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full max-h-[90vh] overflow-y-auto" data-id="element-4869" data-mp="r4c559p0w0uy">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] px-6 py-4 sticky top-0 z-10" data-id="element-4870" data-mp="h3s1y1hy0ma3">
            <div className="flex items-center justify-between" data-id="element-4871" data-mp="c6jrkn31dmje">
              <div data-id="element-4872" data-mp="85g87qls58df">
                <h3 className="text-xl font-bold text-white" data-id="element-4873" data-mp="8699ic1yz5o6">
                  Prescription Details
                </h3>
                <p className="text-sm text-blue-100 mt-1" data-id="element-4874" data-mp="26i6u6vs66rac">{prescription.id}</p>
              </div>
              <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors" data-id="element-4875" data-mp="22gl9wpdb3aq4">
                <XIcon className="w-6 h-6" data-id="element-4876" data-mp="3pem5xxqkqig5" />
              </button>
            </div>
          </div>
          {/* Content */}
          <div className="px-6 py-6 space-y-6" data-id="element-4877" data-mp="3u7fc8q1oxyuc">
            {/* Prescription Source Badge */}
            {getPrescriptionSourceBadge()}
            {/* Status */}
            <div className={`flex items-center justify-between p-4 rounded-lg border ${getStatusColor(prescription.status)}`} data-id="element-4878" data-mp="31hzxbp6wnlt5">
              <div className="flex items-center" data-id="element-4879" data-mp="36inn67jedwls">
                {getStatusIcon(prescription.status)}
                <div className="ml-3" data-id="element-4880" data-mp="3ak990idwtl84">
                  <p className="font-bold" data-id="element-4881" data-mp="3fhr9dkdv2s4i">
                    {getStatusText(prescription.status)}
                  </p>
                  <p className="text-sm" data-id="element-4882" data-mp="pr4cqvw28eau">
                    {new Date(prescription.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                  </p>
                </div>
              </div>
              <CalendarIcon className="w-5 h-5" data-id="element-4883" data-mp="2msa8tbthyipa" />
            </div>
            {/* Prescribed Items with Images and Prices */}
            <div data-id="element-4884" data-mp="2vt8f60rer2b1">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-4885" data-mp="2vsq9yemrpdqz">
                <FileTextIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-4886" data-mp="283nhscuic2i8" />
                Prescribed Items
              </h4>
              <div className="space-y-3" data-id="element-4887" data-mp="2352s37cvt181">
                {prescription.items.map((item, index) => <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-4888" data-mp="2htqkx5vcewua" data-mpi={index}>
                    <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded-lg" data-id="element-4889" data-mp="1pcwcbmdn4w94" />
                    <div className="flex-1" data-id="element-4890" data-mp="3w1ewb36i1fel">
                      <h5 className="font-medium text-gray-900 mb-1" data-id="element-4891" data-mp="3w0mydlpfj5l5">
                        {item.name}
                      </h5>
                      <p className="text-sm text-gray-600" data-id="element-4892" data-mp="4otyulk3m0wn">
                        Quantity: {item.quantity}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-4893" data-mp="3r3d8y621itxb">
                      <p className="text-lg font-bold text-[#1E2B55]" data-id="element-4894" data-mp="3qzzc1dc5bcx9">
                        SAR {item.price.toFixed(2)}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Doctor Info */}
            {prescription.doctor && <div data-id="element-4895" data-mp="ekf4qusaflbo">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-4896" data-mp="ejvnzhedj2z8">
                  <UserIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-4897" data-mp="2ubjht3y3cfdt" />
                  Prescribed By
                </h4>
                <div className="p-3 bg-gray-50 rounded-lg" data-id="element-4898" data-mp="2zbxfpt5za8y4">
                  <p className="text-sm text-gray-700" data-id="element-4899" data-mp="3q0zezibibli6">{prescription.doctor}</p>
                </div>
              </div>}
            {/* Delivery Address or Pickup Branch */}
            {prescription.deliveryAddress && <div data-id="element-4900" data-mp="3h1m5ipnduwxg">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-4901" data-mp="3h0t506f0p3nr">
                  <MapPinIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-4902" data-mp="3c2rl5j9rp0b8" />
                  Delivery Address
                </h4>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-4903" data-mp="dlx3xweba84i">
                  <p className="text-sm text-gray-700 mb-3" data-id="element-4904" data-mp="8l9ehf5qdzce">
                    {prescription.deliveryAddress}
                  </p>
                  {getGoogleMapsLink(prescription.deliveryCoordinates, prescription.deliveryAddress) && <a href={getGoogleMapsLink(prescription.deliveryCoordinates, prescription.deliveryAddress)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#1E2B55] hover:text-[#fdc300] font-medium text-sm" data-id="element-4905" data-mp="wbmxkg6c8nuu">
                      <MapPinIcon className="w-4 h-4" data-id="element-4906" data-mp="1ds0t498owi0p" />
                      View on Google Maps
                      <ExternalLinkIcon className="w-4 h-4" data-id="element-4907" data-mp="1htm41p1nym89" />
                    </a>}
                </div>
              </div>}
            {prescription.pickupBranch && <div data-id="element-4908" data-mp="1rjp6sgyl7ejs">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-4909" data-mp="1rj5mwupdmg34">
                  <StoreIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-4910" data-mp="1wi8tijl08ud7" />
                  Pickup Branch
                </h4>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-4911" data-mp="21ieh9oo5lwq0">
                  <p className="font-medium text-gray-900 mb-1" data-id="element-4912" data-mp="25ioxdu5nu3il">
                    {prescription.pickupBranch.name}
                  </p>
                  <p className="text-sm text-gray-700 mb-3" data-id="element-4913" data-mp="371d8wnua68r1">
                    {prescription.pickupBranch.address}
                  </p>
                  {prescription.pickupTime && <div className="flex items-center gap-2 mb-3 p-2 bg-blue-50 rounded-lg" data-id="element-4914" data-mp="3gx6h5v0yvg30">
                      <ClockIcon className="w-4 h-4 text-blue-600" data-id="element-4915" data-mp="3lpge3v0i1ges" />
                      <span className="text-sm font-medium text-blue-900" data-id="element-4916" data-mp="3qoaukqazusvq">
                        Pickup Time: {prescription.pickupTime}
                      </span>
                    </div>}
                  <a href={getGoogleMapsLink(prescription.pickupBranch.coordinates, prescription.pickupBranch.address)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#1E2B55] hover:text-[#fdc300] font-medium text-sm" data-id="element-4917" data-mp="3is9e69d2v9n">
                    <MapPinIcon className="w-4 h-4" data-id="element-4918" data-mp="22rjtlh3kx97p" />
                    View on Google Maps
                    <ExternalLinkIcon className="w-4 h-4" data-id="element-4919" data-mp="1xyicd2fwgxky" />
                  </a>
                </div>
              </div>}
            {/* Tracking History or Pickup Time */}
            {prescription.trackingHistory && prescription.trackingHistory.length > 0 && <div data-id="element-4920" data-mp="2lnjop0russvi">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-4921" data-mp="2lo34obxy176w">
                    <TruckIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-4922" data-mp="2gqb3ssfbmaqk" />
                    Delivery Tracking
                  </h4>
                  <div className="space-y-4" data-id="element-4923" data-mp="2bsi0q89bdwui">
                    {prescription.trackingHistory.map((event, index) => <div key={index} className="flex gap-4" data-id="element-4924" data-mp="10b9rn9nq3sk0" data-mpi={index}>
                        <div className="flex flex-col items-center" data-id="element-4925" data-mp="109f73wgd62gg">
                          <div className={`w-3 h-3 rounded-full ${index === 0 ? 'bg-green-500' : 'bg-gray-300'}`} data-id="element-4926" data-mp="vbnj3diub0hj" />
                          {index < prescription.trackingHistory.length - 1 && <div className="w-0.5 h-full bg-gray-300 mt-1" data-id="element-4927" data-mp="39k6id9l0k4vl" />}
                        </div>
                        <div className="flex-1 pb-4" data-id="element-4928" data-mp="3ei80nsam8060">
                          <div className="flex items-center justify-between mb-1" data-id="element-4929" data-mp="3el40jm60wyx3">
                            <p className={`font-medium ${index === 0 ? 'text-green-700' : 'text-gray-700'}`} data-id="element-4930" data-mp="2zsa6mvq08g8w">
                              {event.status}
                            </p>
                            <span className="text-xs text-gray-500" data-id="element-4931" data-mp="2vs9qn6n9bwzq">
                              {new Date(event.timestamp).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600" data-id="element-4932" data-mp="2c4ww881noitu">
                            {event.description}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </div>}
            {/* Payment Method */}
            {prescription.paymentMethod && <div data-id="element-4933" data-mp="2uwfy2bjy1qua">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-4934" data-mp="2uwzhxxt5mpay">
                  <CreditCardIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-4935" data-mp="2pzfty66nmy5t" />
                  Payment Method
                </h4>
                <div className="p-3 bg-gray-50 rounded-lg" data-id="element-4936" data-mp="34vx4iv70s8rb">
                  <p className="text-sm text-gray-700" data-id="element-4937" data-mp="2zxn5g9i6co0f">
                    {prescription.paymentMethod}
                  </p>
                </div>
              </div>}
            {/* Total */}
            {prescription.total && <div className="border-t border-gray-200 pt-4" data-id="element-4938" data-mp="126x343iifoli">
                <div className="flex items-center justify-between" data-id="element-4939" data-mp="1711c0sbc66p8">
                  <span className="text-lg font-bold text-gray-900" data-id="element-4940" data-mp="1bzclv31msh7g">Total</span>
                  <span className="text-2xl font-bold text-[#1E2B55]" data-id="element-4941" data-mp="eb2j2lu0tpfa">
                    SAR {prescription.total.toFixed(2)}
                  </span>
                </div>
              </div>}
          </div>
          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex gap-3 sticky bottom-0" data-id="element-4942" data-mp="o87ab02q4b2y">
            <button onClick={onClose} className="flex-1 bg-white border border-gray-300 text-gray-700 font-medium py-2.5 px-4 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4943" data-mp="t8uyldzkidth">
              Close
            </button>
            {prescription.status === 'delivered' && <button className="flex-1 bg-[#1E2B55] hover:bg-[#fdc300] hover:text-[#1E2B55] text-white font-medium py-2.5 px-4 rounded-lg transition-colors" data-id="element-4944" data-mp="3p5stnthawge9">
                Reorder
              </button>}
          </div>
        </div>
      </div>
    </div>;
}